import {
	HeadContent,
	Outlet,
	Scripts,
	createRootRoute,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";
import { ConvexClientProvider } from "@/lib/ConvexClientProvider";
import { initPostHog } from "@/lib/posthog";
import { Sentry } from "@/lib/sentry";
import appCss from "../styles.css?url";

export const Route = createRootRoute({
	head: () => ({
		meta: [
			{ charSet: "utf-8" },
			{ name: "viewport", content: "width=device-width, initial-scale=1" },
			{ title: "Proxi | Errand Service in Lagos, Nigeria" },
			{
				name: "description",
				content:
					"Post any errand in Lagos — groceries, pharmacy runs, document pickups, parcel delivery — and a verified runner nearby handles it. Your money stays protected until you confirm the job is done.",
			},
			{ name: "theme-color", content: "#3E54D3" },
			{ name: "robots", content: "index, follow" },
			{ property: "og:type", content: "website" },
			{ property: "og:site_name", content: "Proxi" },
			{
				property: "og:title",
				content: "Proxi | Errand Service in Lagos, Nigeria",
			},
			{
				property: "og:description",
				content:
					"Post the errand. A verified runner near you handles it. Your money only moves when you confirm the job is done.",
			},
			{ property: "og:image", content: "https://myproxi.ng/og-image.jpg" },
			{ property: "og:url", content: "https://myproxi.ng" },
			{ property: "og:locale", content: "en_NG" },
			{ name: "twitter:card", content: "summary_large_image" },
			{ name: "twitter:site", content: "@myproxi_ng" },
			{
				name: "twitter:title",
				content: "Proxi | Errand Service in Lagos, Nigeria",
			},
			{
				name: "twitter:description",
				content:
					"Post the errand. A verified runner handles it. Your money only moves when you confirm.",
			},
			{ name: "twitter:image", content: "https://myproxi.ng/og-image.jpg" },
		],
		links: [
			{ rel: "preconnect", href: "https://fonts.googleapis.com" },
			{
				rel: "preconnect",
				href: "https://fonts.gstatic.com",
				crossOrigin: "anonymous",
			},
			{
				rel: "stylesheet",
				href: "https://fonts.googleapis.com/css2?family=Caveat:wght@600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap",
			},
			{ rel: "stylesheet", href: appCss },
			{ rel: "canonical", href: "https://myproxi.ng" },
			{
				rel: "icon",
				type: "image/svg+xml",
				href: "/illustrations/logo-mark.svg",
			},
			{ rel: "apple-touch-icon", href: "/illustrations/logo-mark.svg" },
		],
	}),
	component: RootComponent,
	shellComponent: RootDocument,
});

const organizationSchema = {
	"@context": "https://schema.org",
	"@type": "Organization",
	name: "Proxi",
	legalName: "myProxi Digital Solutions Ltd.",
	url: "https://myproxi.ng",
	logo: "https://myproxi.ng/illustrations/logo-mark.svg",
	description:
		"Proxi is an on-demand errand service in Lagos, Nigeria. Customers post errands and verified runners nearby handle them, with payments protected by escrow.",
	areaServed: { "@type": "Country", name: "Nigeria" },
	sameAs: [
		"https://x.com/myproxi_ng",
		"https://www.instagram.com/myproxi.ng",
		"https://www.tiktok.com/@myproxi.ng",
	],
};

function RootDocument({ children }: { children: ReactNode }) {
	return (
		<html lang="en">
			<head>
				<HeadContent />
				<script async src="https://tally.so/widgets/embed.js" />
				<script
					type="application/ld+json"
					// biome-ignore lint/security/noDangerouslySetInnerHtml: JSON-LD schema
					dangerouslySetInnerHTML={{
						__html: JSON.stringify(organizationSchema),
					}}
				/>
			</head>
			<body>
				{children}
				<Scripts />
			</body>
		</html>
	);
}

function RootComponent() {
	const sentryDsn = import.meta.env.VITE_SENTRY_DSN;

	useEffect(() => {
		initPostHog();
	}, []);

	return (
		<ConvexClientProvider>
			{sentryDsn ? (
				<Sentry.ErrorBoundary fallback={<div>Something went wrong.</div>}>
					<Outlet />
				</Sentry.ErrorBoundary>
			) : (
				<Outlet />
			)}
		</ConvexClientProvider>
	);
}
